import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "vet_coupon_dismissed";
const SHOW_DELAY_MS = 12000;

export default function CouponPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [claimed, setClaimed] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(LS_KEY);
    if (dismissed) return;
    const t = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    setOpen(false);
    localStorage.setItem(LS_KEY, "1");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { email, source: "coupon_popup" });
      localStorage.setItem(LS_KEY, "1");
      setClaimed(true);
      toast.success("Coupon code unlocked!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let description = "Please try again or call us.";
      if (Array.isArray(detail) && detail[0]?.msg) {
        description = `Email looks invalid — please double-check it.`;
      } else if (typeof detail === "string") {
        description = detail;
      }
      toast.error("Couldn't save your email", { description });
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="coupon-popup"
      className="fixed inset-0 z-[60] flex items-end md:items-center justify-center px-4 pb-20 md:pb-4 bg-[#0B132B]/70 backdrop-blur-sm"
    >
      <div className="relative w-full max-w-lg bg-white border-l-4 border-[#D32F2F] shadow-2xl">
        <button
          onClick={close}
          data-testid="coupon-close-btn"
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center text-[#4A5568] hover:bg-[#F8F9FA]"
        >
          <X className="w-5 h-5" />
        </button>

        {!claimed ? (
          <div className="p-8 md:p-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 bg-[#D32F2F] flex items-center justify-center">
                <Gift className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <span className="font-mono-label text-[#D32F2F]">
                First-Time Customer Offer
              </span>
            </div>
            <h3 className="font-display text-3xl md:text-4xl font-bold text-[#0B132B] leading-[1.05]">
              Save <span className="text-[#D32F2F]">$25</span> on your first visit.
            </h3>
            <p className="mt-4 text-[#4A5568] leading-relaxed">
              Drop your email and we'll send you a coupon code to bring in on your
              first service. No spam — just a one-time code and occasional
              maintenance reminders for your vehicle.
            </p>
            <form onSubmit={submit} data-testid="coupon-form" className="mt-6 space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                data-testid="coupon-email-input"
                className="w-full h-12 px-4 border border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#D32F2F] focus:border-[#D32F2F] text-[#0B132B]"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="coupon-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#D32F2F] hover:bg-[#B71C1C] disabled:opacity-60 text-white font-semibold py-4 rounded-none transition-colors"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Gift className="w-5 h-5" strokeWidth={2.5} />}
                {loading ? "Unlocking..." : "Claim My $25 Off"}
              </button>
              <button
                type="button"
                onClick={close}
                className="w-full text-center text-sm text-[#4A5568] hover:text-[#0B132B] py-2"
              >
                No thanks, I'll pay full price
              </button>
            </form>
          </div>
        ) : (
          <div className="p-8 md:p-10 text-center">
            <div className="w-14 h-14 bg-[#D32F2F] mx-auto flex items-center justify-center mb-4">
              <Gift className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-[#0B132B]">
              Your code is ready.
            </h3>
            <p className="mt-3 text-[#4A5568]">Show this code at check-in:</p>
            <div className="mt-5 mx-auto inline-block border-2 border-dashed border-[#D32F2F] px-6 py-4">
              <div className="font-display text-3xl font-bold tracking-[0.2em] text-[#0B132B]">
                VET25
              </div>
              <div className="mt-1 font-mono-label text-[#D32F2F]">$25 OFF · FIRST-TIME VISIT</div>
            </div>
            <p className="mt-5 text-xs text-[#4A5568]">
              Valid on your first service. Not combinable with other offers. One per customer.
            </p>
            <button
              onClick={close}
              data-testid="coupon-done-btn"
              className="mt-6 inline-flex items-center justify-center gap-2 bg-[#0B132B] hover:bg-[#141e3c] text-white font-semibold py-3 px-8 rounded-none"
            >
              Got it, thanks!
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
